import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import AntiSpamHTTPMapping     from "./HTTP/AntiSpamHTTPMapping";
import AntiSpamHTTPService     from "./HTTP/AntiSpamHTTPService";
import AccountSendersData      from "./Entities/AccountSendersData";
import CollectionList          from "@/js/Shared/Collections/CollectionList";
import Controller              from "@/js/Shared/Controllers/Controller";

export { AntiSpamController as default };

/**
* Email AntiSpam Controller
*
* @author      rsommerfeld <roni.s.sommerfeld@altice.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class AntiSpamControllerSingleton extends Controller {
    /**
     * AntiSpamData Data
     *
     * @public
     *
     * @type {CollectionList}
     *
     * @since 7.0.0
     */
    data = new AccountSendersData();

    /**
     * HTTP Mapping object
     *
     * @private
     *
     * @type {AntiSpamHTTPMapping}
     *
     * @since 7.0.0
     */
    http_mapping = new AntiSpamHTTPMapping();

    /**
     * HTTP Service object
     *
     * @private
     *
     * @type {AntiSpamHTTPService}
     *
     * @since 7.0.0
     */
    http_service = new AntiSpamHTTPService();

    /**
     * Defines if whitelist was fetched
     *
     * @public
     *
     * @type {Boolean}
     *
     * @since 7.0.0
     */
    whitelist_fetched = false;

    /**
     * Defines if blacklist was fetched
     *
     * @public
     *
     * @type {Boolean}
     *
     * @since 7.0.0
     */
    blacklist_fetched = false;

    /**
     * Fetchs List whitelist AntiSpam from APi Service only once
     *
     * If already fetched, just return true
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {Promise<Boolean>}
     */
    async fetchWhitelistOnce(reload = false) {
        if (this.whitelist_fetched && !reload) {
            return true;
        }

        return this.fetchWhitelist();
    }

    /**
     * Fetchs List whitelist AntiSpam from APi Service
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {Promise.<boolean>}
     */
    async fetchWhitelist() {
        this.content_state.setLoading();
        let response = await this.http_service.fetchWhitelist();

        if (response.success === false) {
            NotificationsController.addMessage('notifications.', response.messages);
            this.content_state.setLoadedState(response.success);
            return false;
        }

        this.http_mapping.mapWhitelistList(this.data, response);
        this.content_state.setLoadedState(response.success);
        this.whitelist_fetched = true;

        return response.success;
    }

    /**
     * Remove email from whitelist antispam
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {String} sender email to remove whitelist
     *
     * @return {Promise.<boolean>}
     */
    async removeWhitelistSender(sender) {
        this.saving_state.setSaving(sender);
        let response = await this.http_service.removeWhitelistSender(sender);
        NotificationsController.addMessage('notifications.', response.messages);

        if(response.success){
            this.http_mapping.mapWhitelistRemoveItem(this.data, response);
        }

        this.saving_state.unsetSaving(sender);

        return response.success;
    }

    /**
     * Adds email to whitelist antispam
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Object} senders senders to add to whitelist
     *
     * @return {Promise.<boolean>}
     */
    async addWhitelistSenders(senders) {
        this.content_state.setSaving(true);
        let response = await this.http_service.addWhitelistSender(senders);
        NotificationsController.addMessage('notifications.', response.messages);

        if(response.success){
            this.http_mapping.mapWhitelistAddItem(this.data, response);
        }

        this.content_state.setSaving(false);
        return response.success;
    }

    /**
     * Fetchs List blacklist AntiSpam from APi Service only once
     *
     * If already fetched, just return true
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {Promise<Boolean>}
     */
    async fetchBlacklistOnce() {
        if (this.blacklist_fetched) {
            return true;
        }

        return this.fetchBlacklist();
    }


    /**
     * Fetchs List blacklist AntiSpam from APi Service
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {Promise.<boolean>}
     */
    async fetchBlacklist() {
        this.content_state.setLoading();
        let response = await this.http_service.fetchBlacklist();

        if (response.success === false) {
            NotificationsController.addMessage('notifications.', response.messages);
            this.content_state.setLoadedState(response.success);
            return false;
        }

        this.http_mapping.mapBlacklistList(this.data, response);
        this.content_state.setLoadedState(response.success);
        this.blacklist_fetched = true;

        return response.success;
    }

    /**
     * Remove email from whitelist antispam
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {String} sender email to remove whitelist
     *
     * @return {Promise.<boolean>}
     */
    async removeBlacklistSender(sender) {
        this.saving_state.setSaving(sender);
        let response = await this.http_service.removeBlacklistSender(sender);
        NotificationsController.addMessage('notifications.', response.messages);

        if(response.success){
            this.http_mapping.mapBlacklistRemoveItem(this.data, response);
        }

        this.saving_state.unsetSaving(sender);

        return response.success;
    }

    /**
     * Adds email to whitelist antispam
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Object} senders senders to add to blacklist
     *
     * @return {Promise.<boolean>}
     */
    async addBlacklistSenders(senders) {
        this.content_state.setSaving(true);
        let response = await this.http_service.addBlacklistSender(senders);
        NotificationsController.addMessage('notifications.', response.messages);

        if(response.success){
            this.http_mapping.mapBlacklistAddItem(this.data, response);
        }

        this.content_state.setSaving(false);

        return response.success;
    }
}

/**
 * @type {AntiSpamControllerSingleton}
 */
const AntiSpamController = new AntiSpamControllerSingleton();